/* eslint-disable @stylistic/jsx-wrap-multilines */
import { Button } from '@/components/common/Button';
import { useStore } from '@/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import liff from '@line/liff';
import { flowResult } from 'mobx';
import { ROUTES } from '@/configs/constants';
import { useLocation } from 'react-router-dom';
import iconQr from '../../assets/images/qr.png';
import { CONST_BUILD, keyStorage } from '@/constant/string';
import { getStorage, removeStorage, saveLocalStorage } from '@/utils/browsers';
import { Customer } from '@/types/auth';
import { ThreeCircles } from 'react-loader-spinner';
import logo from '../../assets/images/logo_red.png';

function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
}

export default observer(function LoginPage() {
    const [currentInvitationId, setCurrentInvitationId] = useState<String>('');
    const [checking, setChecking] = useState<boolean>(true);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const query = useQuery();
    const invitationId = query.get('invitation');
    const {
        authStore: { signinQrCode, setLiffID, clearAuthentication, showTermOfUse, setTermOfUse },
        apiStore: { setLoading },
        modalStore: { showErrorModal, hideModal }
    } = useStore();

    let loging = false;

    useEffect(() => {
        const currentVersion = CONST_BUILD.VERSION;
        const storedVersion = getStorage(keyStorage.APP_VERSION);
        if (storedVersion !== currentVersion) {
            saveLocalStorage(keyStorage.APP_VERSION, currentVersion);
            setTimeout(() => {
                window.location.reload();
            }, 240);
        } else {
            saveLocalStorage(keyStorage.APP_VERSION, currentVersion);
        }
    }, []);

    useEffect(() => {
        checkAuth();
        loging = true;
    }, []);
    useEffect(() => {
        if (showTermOfUse) {
            setTermOfUse(false);
            handleLogin();
        }
    }, [showTermOfUse]);

    useEffect(() => {
        if (invitationId && invitationId !== currentInvitationId) {
            setCurrentInvitationId(invitationId);
            handleInvitation(invitationId);
        }
    }, [invitationId]);

    useEffect(() => { // handle close liff app when press back button
        window.addEventListener('popstate', () => {
            try {
                liff.closeWindow();
            } catch (error) {

            }
        });
        const urlParams = new URLSearchParams(window.location.search);
        const liffStateEncoded = urlParams.get('liff.state');
        if (liffStateEncoded) {
            const liffStateDecodedOnce = decodeURIComponent(liffStateEncoded);
            const liffStateDecodedTwice = decodeURIComponent(liffStateDecodedOnce);
            const stateParams = new URLSearchParams(liffStateDecodedTwice);
            const liff_id = stateParams.get(keyStorage.LIFF_ID);
            const uuid_from_params = stateParams.get('user_uuid');
            const current_uuid = getStorage(keyStorage.UUID);
            const oldLiffId = getStorage(keyStorage.LIFF_ID);
            if ((liff_id && oldLiffId && liff_id !== oldLiffId) ||
                (uuid_from_params && current_uuid && uuid_from_params !== current_uuid)) {
                showErrorModal({
                    id: 'liff_id_error',
                    cancelButton: false,
                    saveButton: false,
                    showCloseIcon: false,
                    content: (
                        <div className='w-full text-start'>
                            <p className='mt-6 text-[14px] text-black'>{t('messages.scan_QR_with_Camera_app')}</p>
                        </div>
                    )
                });
                setTimeout(() => {
                    removeStorage([keyStorage.TOKEN, keyStorage.PROFILE_USER, keyStorage.LINE_PROFILE_LINK, keyStorage.UUID, keyStorage.LIFF_ID]);
                    try {
                        liff.closeWindow();
                    } catch (error) {
                    }
                }, 2000);
                return;
            }

            if (!oldLiffId && liff_id) {
                setLiffID(liff_id);
                saveLocalStorage(keyStorage.LIFF_ID, liff_id);
            }
        }
    }, []);

    const checkAuth = async () => { // handle auto login when have access_token
        if (loging) {
            return;
        }
        const token = getStorage(keyStorage.TOKEN);
        const profile: Customer = getStorage(keyStorage.PROFILE_USER, true);
        const uuid = getStorage(keyStorage.UUID);
        if (token && profile && uuid) {
            loging = false;
            setTimeout(() => {
                const urlParams = new URLSearchParams(window.location.search);
                const liffState = urlParams.get('liff.state');
                if (liffState) {
                    const decodedState = decodeURIComponent(liffState);
                    const stateParams = new URLSearchParams(decodedState);
                    const invoiceId = stateParams.get('invoiceId');
                    if (invoiceId) {
                        navigate(ROUTES.viewInvoice.href, { state: { id: invoiceId, fromMessage: true } });
                    } else {
                        navigate(ROUTES.home.href, { state: { isShowModal: false, checkIdLine: true } });
                    }
                } else navigate(ROUTES.home.href, { state: { isShowModal: false, checkIdLine: true } });
                setChecking(false);
            }, 240);
        } else setChecking(false);
    };

    const handleInvitation = (invitationId: String) => {
    };
    const getUserUUID = (url) => {
        if (!url) {
            return false;
        }
        const urlParams = new URLSearchParams(new URL(url).search);
        return urlParams.get('user_uuid');
    };

    const getLiffIdFromQr = (url) => {
        if (!url) {
            return false;
        }
        const urlParams = new URLSearchParams(new URL(url).search);
        return urlParams.get('liff_id');
    };

    const openLink = () => {
        hideModal('popup_tof');
        setTermOfUse(true);
        navigate(ROUTES.termOfService.href);
    };

    const handleLogin = async () => {
        setLoading(true);
        let liff_id = getStorage(keyStorage.LIFF_ID);
        if (!liff_id) { // check liff_id from storage
            setLoading(false);
            showErrorModal({
                content: t('messages.scan_QR_with_Camera_app')
            });
            return;
        }
        if (!liff) { // check liff init success
            setLoading(false);
            showErrorModal({
                content: t('messages.liff_error')
            });
            return;
        }
        if (!liff.isInClient()) { // check app is open in Line app
            setLoading(false);
            showErrorModal({
                content: t('messages.not_in_Line')
            });
            return;
        }
        const accessToken = liff.getIDToken(); // get Line's access_token to get Line's profile
        if (!accessToken) {
            setLoading(false);
            showErrorModal({
                content: t('messages.liff_error')
            });
            return;
        }
        if (liff.isApiAvailable('scanCodeV2')) {
            liff.getFriendship().then((data) => {
                if (data.friendFlag) {
                    setLoading(false);
                    showErrorModal({
                        id: 'popup_tof',
                        title: t('title.terms_of_service'),
                        showCloseIcon: true,
                        cancelButton: false,
                        saveButton: false,
                        content:
                            <div>
                                <p className='mt-6 text-[14px] text-black text-left'>・本サービスは、LINEを通じて請求書等の文書の受け取り及び閲覧ができるサービスです。</p>
                                <p className='text-[14px] text-black text-left'>
                                    <span>・本サービスをご利用いただくにあたり、重要な事項を記載しておりますので、必ず利用規約[</span>
                                    <span className='text-blue-600 underline mt-2' onClick={() => openLink()}>リンク</span>
                                    <span>]をご確認ください。</span>
                                </p>
                                <p className='text-[14px] text-black text-left'>
                                    <span>・「同意する」をクリックすることにより、利用規約[</span>
                                    <span className='text-blue-600 underline mt-2' onClick={() => openLink()}>リンク</span>
                                    <span>]に同意したこととなります。</span>
                                </p>
                                <div className='flex flex-row text-center mt-8 w-full justify-around'>
                                    <Button
                                        onClick={() => {
                                            setLoading(true);
                                            handleScanQrCode(liff_id, accessToken);
                                        }}
                                        className='min-w-32 bg-main'
                                    >
                                        {t('text.agree')}
                                    </Button>
                                </div>
                            </div>
                    });
                } else {
                    setTimeout(() => {
                        handleScanQrCode(liff_id, accessToken);
                    }, 1000);
                }
            });
        } else {
            setTimeout(() => {
                handleLogin();
            }, 1000);
        }
    };

    const handleScanQrCode = async (liff_id: any, token: string) => {
        hideModal('popup_tof');
        if (!liff_id) return;
        if (liff.isApiAvailable('scanCodeV2')) {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
            const result = await liff.scanCodeV2(); // open module scan QR code
            if (!result) {
                return;
            }
            if (!result.value) { // check QR code's value
                showErrorModal({
                    content: t('messages.QR_invalid')
                });
                return;
            }
            const uuid = getUserUUID(result.value); // get user_uuid from QR code
            if (!uuid) {
                showErrorModal({
                    content: t('messages.QR_invalid')
                });
                return;
            }
            const liffIdFromQr = getLiffIdFromQr(result.value); // get liff_id from QR code
            if (!liffIdFromQr || liffIdFromQr !== liff_id) { // check liff_id from QR code with liff_if inited
                clearAuthentication();
                showErrorModal({
                    content: t('messages.scan_QR_with_Camera_app')
                });
                return;
            }
            const payload = { user_uuid: uuid, id_token: token, liff_id: liff_id };
            const resLogin = await flowResult(signinQrCode(payload));
            if (resLogin) { // login success
                saveLocalStorage(keyStorage.UUID, uuid);
                setTimeout(() => {
                    navigate(ROUTES.home.href, { state: { isShowModal: false, checkIdLine: false } });
                }, 240);
            } else { // login failed
                setTimeout(() => {
                    showErrorModal({
                        content: t('messages.sign_in_failed')
                    });
                }, 240);
            }
        } else {
            setTimeout(() => {
                handleScanQrCode(liff_id, token);
            }, 240);
        }
    };

    return (
        <div className='w-full h-full '>
            {checking ?
                (
                    <div className='fixed w-screen h-screen top-0 left-0 z-[9999] bg-white flex items-center justify-center'>
                        <ThreeCircles
                            width={60}
                            height={60}
                            color=''
                        />
                    </div>
                ) :
                (
                    <div className='w-full  fixed top-[30%]'>
                        <div className='w-full flex justify-center'>
                            <img className='w-2/3' src={logo} alt={logo} />
                        </div>
                        <div className='w-full flex justify-center mt-14'>
                            <Button className='bg-main text-white w-[376px] h-[72px] rounded-xl flex' onClick={() => handleLogin()}>
                                <img src={iconQr} className='mr-[16px] w-[18px] h-[18px]' alt={iconQr} />
                                <p className='text-[22px] font-light'>QRコードでログイン</p>
                            </Button>
                        </div>
                    </div>
                )}
        </div>
    );
});
